package ru.villawonka.app.presentation.sharedviews.postList.compose

import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.navigator.bottomSheet.BottomSheetNavigator
import cafe.adriel.voyager.navigator.bottomSheet.LocalBottomSheetNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import org.koin.compose.koinInject
import ru.villawonka.app.getTelegram
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.presentation.screens.conversationDetails.compose.ConversationDetailsScreen
import ru.villawonka.app.presentation.screens.photoSlider.compose.PhotoSliderScreen
import ru.villawonka.app.presentation.screens.postCard.compose.PostCardScreen
import ru.villawonka.app.presentation.sharedviews.postList.viewmodel.PostListUIEvent
import ru.villawonka.app.presentation.sharedviews.postList.viewmodel.PostListViewModel

@Composable
fun PostListView(
    topPadding: Dp = 0.dp,
    bottomPadding: Dp = 0.dp,
    title: String,
    posts: List<PostUIModel>,
    onLoadSimilar: (PostUIModel) -> Unit,
    onLoadNextPage: suspend () -> Unit,
    hasMoreData: Boolean,
    listState: LazyListState = rememberLazyListState(),
    navigator: Navigator? = LocalNavigator.currentOrThrow.parent,
    bottomSheetNavigator: BottomSheetNavigator = LocalBottomSheetNavigator.current,
) {
    val viewModel = koinInject<PostListViewModel>()

    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            when (event) {
                is PostListUIEvent.ScrollToUp ->
                    listState.scrollToItem(0)
                is PostListUIEvent.PhoneCall ->
                    getTelegram()?.openWindowLink("tel:${event.phoneNumber}")
                is PostListUIEvent.OpenExternalLink ->
                    getTelegram()?.openTelegramLink(event.link)
                is PostListUIEvent.OpenMediaSliderScreen ->
                    navigator?.push(PhotoSliderScreen(
                        initialPage = event.initialPage,
                        photos = event.media
                    ))
                is PostListUIEvent.OpenConversationDetailsScreen ->
                    bottomSheetNavigator.show(ConversationDetailsScreen(event.conversationDetails))
                is PostListUIEvent.OpenPostCardScreen -> {
                    navigator?.push(PostCardScreen(event.postId, event.postType))
                }
            }
        }
    }


    LaunchedEffect(hasMoreData) {
        snapshotFlow { listState.layoutInfo }
            .collect { layoutInfo ->
                if (!hasMoreData) return@collect

                val lastVisibleItem = layoutInfo.visibleItemsInfo.lastOrNull()
                if (lastVisibleItem != null
                    && layoutInfo.totalItemsCount > 0
                    && lastVisibleItem.index >= layoutInfo.totalItemsCount - 3
                ) {
                    onLoadNextPage()
                }
            }
    }


    PostListRootLayout(
        topPadding = topPadding,
        bottomPadding = bottomPadding,
        title = title,
        listState = listState,
        posts = posts,
        callbacks = remember {
            PostListCallbacks(
                onPostClick = viewModel::onPostClick,
                onConversionDetailsClick = viewModel::onConversionDetailsClick,
                onLikeButtonClick = viewModel::onLikeButtonClick,
                onChatButtonClick = viewModel::onChatButtonClick,
                onCallButtonClick = viewModel::onCallButtonClick,
                onImageEnlargeButtonClick = viewModel::onImageEnlargeButtonClick,
                onScrollToUpButtonClick = viewModel::onScrollToUpButtonClick,
                onShowSimilarButtonClick = onLoadSimilar
            )
        }
    )
}

data class PostListCallbacks(
    val onPostClick: (PostUIModel) -> Unit,
    val onConversionDetailsClick: (PostUIModel) -> Unit,
    val onLikeButtonClick: (PostUIModel, Boolean) -> Unit,
    val onChatButtonClick: (PostUIModel) -> Unit,
    val onCallButtonClick: (PostUIModel) -> Unit,
    val onImageEnlargeButtonClick: (PostUIModel, Int) -> Unit,
    val onShowSimilarButtonClick: (PostUIModel) -> Unit,
    val onScrollToUpButtonClick: () -> Unit,
)